import React from 'react';
import {
  Drawer, Box, Typography, IconButton, Button, Divider, Chip,
  CircularProgress, Tooltip,
} from '@mui/material';
import {
  Close, DoneAll, NotificationsNone, Campaign, Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationService, authService } from '../services/api';

const TYPE_META = {
  System: { label: 'System', color: '#1565c0', bg: '#e3f2fd', icon: <SettingsIcon sx={{ fontSize: 16 }} /> },
  User:   { label: 'User',   color: '#6a1b9a', bg: '#f3e5f5', icon: <Campaign     sx={{ fontSize: 16 }} /> },
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const NotificationPanel = ({ open, onClose, notifications, loading, onDismiss, onDismissAll }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const systemNotifs = notifications.filter(n => n.type === 'System');
  const userNotifs   = notifications.filter(n => n.type === 'User');

  const renderGroup = (items, label, meta) => {
    if (items.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, bgcolor: meta.bg }}>
          {meta.icon}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {label} ({items.length})
          </Typography>
        </Box>
        {items.map(n => (
          <Box
            key={n.id}
            sx={{
              px: 2, py: 1.5,
              borderBottom: '1px solid #f0f0f0',
              '&:hover': { bgcolor: '#fafafa' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1, pr: 1, lineHeight: 1.4 }}>
                {n.title}
              </Typography>
              <Tooltip title="Dismiss">
                <IconButton size="small" onClick={() => onDismiss(n.id)} sx={{ mt: -0.5, mr: -0.5 }}>
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {n.description && (
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.3, lineHeight: 1.5 }}>
                {n.description}
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.5 }}>
              {timeAgo(n.createdAt)}
              {n.endDate && ` · Expires ${new Date(n.endDate).toLocaleDateString('en-IN')}`}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.5,
        background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
        color: '#fff',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsNone />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Notifications</Typography>
          {notifications.length > 0 && (
            <Chip
              label={notifications.length}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', height: 20, fontSize: '0.72rem' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {notifications.length > 0 && (
            <Tooltip title="Mark all read">
              <IconButton size="small" color="inherit" onClick={onDismissAll}>
                <DoneAll sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
            <NotificationsNone sx={{ fontSize: 56, color: '#e0e0e0', mb: 1 }} />
            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              You're all caught up!
            </Typography>
            <Typography color="text.disabled" sx={{ fontSize: '0.78rem', mt: 0.5 }}>
              No new notifications
            </Typography>
          </Box>
        ) : (
          <>
            {renderGroup(systemNotifs, 'System Notifications', TYPE_META.System)}
            {renderGroup(userNotifs,   'User Notifications',   TYPE_META.User)}
          </>
        )}
      </Box>

      {/* Footer — admin link */}
      {isAdmin && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              onClick={() => { navigate('/admin/notifications'); onClose(); }}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Manage Notifications
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default NotificationPanel;
